import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Typography, } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
export const TEMPLATE_FIELDS = [
    { id: "resume", label: "View Resume", section: "Candidate" },
    { id: "linkedin", label: "LinkedIn profile", section: "Candidate" },
    { id: "native", label: "Native", section: "Candidate" },
    { id: "residing", label: "Residing", section: "Candidate" },
    { id: "commute", label: "Commute", section: "Candidate" },
    { id: "age", label: "Age", section: "Candidate" },
    { id: "family", label: "Family", section: "Candidate" },
    { id: "maritalStatus", label: "Marital Status", section: "Candidate" },
    { id: "experience", label: "Experience", section: "Professional" },
    { id: "ctc", label: "CTC", section: "Professional" },
    { id: "ectc", label: "ECTC", section: "Professional" },
    { id: "recomctc", label: "Recommended CTC", section: "Professional" },
    { id: "reason", label: "Reason", section: "Professional" },
    { id: "noticep", label: "Notice Period", section: "Professional" },
    { id: "whenjoin", label: "When can join", section: "Professional" },
    { id: "coreskills", label: "Core Skills", section: "Professional", full: true },
    { id: "projects", label: "Projects", section: "Professional" },
    { id: "certifications", label: "Certifications", section: "Professional" },
    { id: "commskills", label: "Communication Skills", section: "Assessment" },
    { id: "stability", label: "Stability", section: "Assessment" },
    { id: "attitude", label: "Attitude / DISC Summary", section: "Assessment" },
    { id: "education", label: "Education", section: "Assessment" },
    { id: "hobbies", label: "Hobbies", section: "Assessment" },
    { id: "bond", label: "Bond", section: "Assessment" },
    { id: "remarks", label: "Remarks", section: "Assessment", multiline: true, full: true },
];
export const SECTIONS = ["Candidate", "Professional", "Assessment"];
export const EMPTY_TEMPLATE = Object.fromEntries(TEMPLATE_FIELDS.map(f => [f.id, ""]));
const LABEL_MAP = {
    resume: "View Resume",
    linkedin: "LinkedIn profile",
    native: "Native",
    residing: "Residing",
    commute: "Commute",
    age: "Age",
    family: "Family",
    maritalStatus: "Marital Status",
    experience: "Experience",
    ctc: "CTC",
    ectc: "ECTC",
    recomctc: "Recommended CTC",
    reason: "Reason",
    noticep: "Notice Period",
    whenjoin: "When can join",
    coreskills: "Core Skills",
    projects: "Projects",
    certifications: "Certifications",
    commskills: "Communication Skills",
    stability: "Stability",
    attitude: "Attitude / DISC Summary",
    education: "Education",
    hobbies: "Hobbies",
    bond: "Bond",
    remarks: "Remarks",
};
const KNOWN_LABELS = Object.values(LABEL_MAP).map(l => l.replace(/\./g, "\\."));
const BLEED_PATTERN = new RegExp(`^(${KNOWN_LABELS.join("|")}):\\s*`, "i");
function stripBleed(value) {
    const match = value.match(BLEED_PATTERN);
    return match ? "" : value; // if the whole value IS another field's line, treat this field as empty
}
// Unique per-field autocomplete token so the browser can't group/sync fields
// (autoComplete="new-password" on every field was causing Chrome to treat
// them as a password-confirmation group and mirror values across fields)
const noAutofill = (id) => `off-${id}-no-fill`;
export function buildFormattedText(vals) {
    const round = vals.__round || "";
    const screener = vals.__screener || "";
    const sep = "-".repeat(77);
    let lines = `*\n${round} "${screener}"\n${sep}\n`;
    TEMPLATE_FIELDS.forEach(({ id }) => {
        lines += `${LABEL_MAP[id]}: ${vals[id] || ""}\n`;
    });
    return lines.trim();
}
export function parseFormattedText(text) {
    if (!text)
        return null;
    const result = { ...EMPTY_TEMPLATE };
    const headerMatch = text.match(/^(.+?)\s+"(.+?)"/m);
    if (headerMatch) {
        result.__round = headerMatch[1].trim();
        result.__screener = headerMatch[2].trim();
    }
    const aliasMap = [
        ['resume', ['View Resume']],
        ['linkedin', ['LinkedIn profile', 'LinkedIn']],
        ['native', ['Native']],
        ['residing', ['Residing']],
        ['commute', ['Commute']],
        ['age', ['Age']],
        ['family', ['Family']],
        ['maritalStatus', ['Marital Status']],
        ['experience', ['Experience']],
        ['ctc', ['CTC']],
        ['ectc', ['ECTC']],
        ['recomctc', ['Recommended CTC', 'Recom. CTC']],
        ['reason', ['Reason', 'Reason for leaving']],
        ['noticep', ['Notice Period']],
        ['whenjoin', ['When can join']],
        ['coreskills', ['Core Skills']],
        ['projects', ['Projects']],
        ['certifications', ['Certifications']],
        ['commskills', ['Communication Skills']],
        ['stability', ['Stability']],
        ['attitude', ['Attitude / DISC Summary', 'Attitude']],
        ['education', ['Education']],
        ['hobbies', ['Hobbies']],
        ['bond', ['Bond']],
        ['remarks', ['Remarks', 'Remarks']],
    ];
    for (const [id, labels] of aliasMap) {
        for (const label of labels) {
            const escaped = label.replace(/\./g, "\\.");
            const match = text.match(new RegExp(`^${escaped}:\\s*(.*)$`, "m"));
            if (match) {
                result[id] = stripBleed(match[1].trim());
                break;
            }
        }
    }
    return result;
}
// Renders saved feedback with bold section/field headings and real spacing
// instead of dumping the raw template string as one flat block of
// "Label: value" lines. Falls back to the raw text when it wasn't built via
// the template (e.g. manually typed notes) — parseFormattedText only finds
// a round/screener header when the text actually matches the template's
// own format. Shared by the HR Round tab, the Interview Round tab, and the
// public interviewer feedback page, so previously-saved feedback always
// reads the same way everywhere it's shown.
export const FormattedFeedback = ({ text }) => {
    const parsed = parseFormattedText(text);
    if (!parsed?.__round) {
        return (_jsx("p", { className: "text-sm text-gray-800 whitespace-pre-wrap leading-relaxed font-mono", children: text }));
    }
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("p", { className: "text-sm font-bold text-gray-800", children: [parsed.__round, parsed.__screener && _jsxs("span", { className: "font-normal text-gray-500", children: [" \u2014 ", parsed.__screener] })] }), SECTIONS.map((section) => (_jsxs("div", { children: [_jsx("p", { className: "text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-2 pb-1 border-b border-gray-100", children: section }), _jsx("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2", children: TEMPLATE_FIELDS.filter((f) => f.section === section && f.id !== 'remarks').map((f) => (_jsxs("p", { className: `text-sm text-black ${'full' in f && f.full ? 'sm:col-span-2' : ''}`, children: [_jsxs("span", { className: "font-bold text-gray-900", children: [f.label, ": "] }), parsed[f.id] ? _jsx("span", { className: "font-normal", children: parsed[f.id] }) : _jsx("span", { className: "font-normal text-gray-400 italic", children: "\u2014" })] }, f.id))) })] }, section))), _jsxs("div", { className: "bg-gray-50 border border-gray-200 rounded-lg p-3", children: [_jsx("p", { className: "text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-1.5", children: "Remarks" }), _jsx("p", { className: "text-sm font-normal text-black whitespace-pre-wrap", children: parsed.remarks || _jsx("span", { className: "text-gray-400 italic", children: "\u2014" }) })] })] }));
};
export const TemplateModal = ({ open, onClose, onInsert, screenerName, existingText, defaultRound, title, defaultResume, defaultLinkedin }) => {
    const [vals, setVals] = useState(() => {
        const parsed = parseFormattedText(existingText);
        return parsed
            ? { ...parsed, resume: parsed.resume || defaultResume || "", linkedin: parsed.linkedin || defaultLinkedin || "" }
            : { ...EMPTY_TEMPLATE, __round: defaultRound || "HR Round", __screener: screenerName || "", resume: defaultResume || "", linkedin: defaultLinkedin || "" };
    });
    const [preview, setPreview] = useState(false);
    useEffect(() => {
        if (!open)
            return;
        const parsed = parseFormattedText(existingText);
        if (parsed) {
            setVals({
                ...parsed,
                // Always prefer the passed props over whatever was parsed from text
                __round: defaultRound || parsed.__round || "HR Round",
                __screener: screenerName || parsed.__screener || "",
                resume: parsed.resume || defaultResume || "",
                linkedin: parsed.linkedin || defaultLinkedin || "",
            });
        }
        else {
            setVals({
                ...EMPTY_TEMPLATE, __round: defaultRound || "HR Round", __screener: screenerName || "",
                resume: defaultResume || "", linkedin: defaultLinkedin || "",
            });
        }
    }, [open, existingText, screenerName, defaultRound, defaultResume, defaultLinkedin]);
    const set = (id) => (e) => setVals(prev => ({ ...prev, [id]: e.target.value }));
    const fieldSx = {
        "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": {
            fontSize: "0.85rem", py: "8px", px: "10px", lineHeight: 1.5,
        },
        "& fieldset": { borderColor: "#d0daea" },
        bgcolor: "#fff",
    };
    const labelSx = {
        fontSize: "0.72rem", fontWeight: 700, color: "#1a1a2e",
        textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5,
    };
    const handleInsert = () => {
        onInsert(buildFormattedText(vals));
        onClose();
    };
    const handleClear = () => setVals({ ...EMPTY_TEMPLATE, __round: defaultRound || "HR Round", __screener: screenerName || "" });
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "md", fullWidth: true, PaperProps: { sx: { borderRadius: 2, maxHeight: "90vh" } }, children: [_jsxs(DialogTitle, { sx: {
                    background: "linear-gradient(135deg, #597e37 0%, #3d5420 100%)",
                    color: "#fff", py: 1.5, fontSize: "0.9rem", fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                }, children: [title || 'HR Feedback Template', _jsxs(Box, { sx: { display: "flex", gap: 1, alignItems: "center" }, children: [_jsx(Box, { sx: { display: "flex", bgcolor: "rgba(255,255,255,0.15)", borderRadius: 1, p: "2px", gap: "2px" }, children: ["Edit", "Preview"].map((tab, i) => (_jsx(Box, { onClick: () => setPreview(i === 1), sx: {
                                        px: 1.5, py: 0.4, borderRadius: 0.8, cursor: "pointer",
                                        fontSize: "0.72rem", fontWeight: 600,
                                        bgcolor: preview === (i === 1) ? "rgba(255,255,255,0.25)" : "transparent",
                                        color: "#fff", transition: "background 0.15s",
                                        "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                                    }, children: tab }, tab))) }), _jsx(IconButton, { onClick: onClose, size: "small", sx: { color: "#fff" }, children: _jsx(CloseIcon, { fontSize: "small" }) })] })] }), _jsx(DialogContent, { dividers: true, sx: { p: 0, overflowY: "auto" }, children: preview ? (_jsx(Box, { sx: { p: 2.5 }, children: _jsx(Box, { sx: {
                            bgcolor: "#f8fafd", border: "1px solid #e4eaf4", borderRadius: 1.5,
                            p: 2, fontFamily: "monospace", fontSize: "0.78rem",
                            color: "#1a1a2e", whiteSpace: "pre-wrap", lineHeight: 1.8,
                        }, children: buildFormattedText(vals) }) })) : (_jsxs(Box, { component: "form", autoComplete: "off", noValidate: true, sx: { p: 2.5 }, children: [_jsxs(Box, { sx: {
                                display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2,
                                bgcolor: "#eef8f1", border: "1px solid #cdeadb", borderRadius: 1.5, p: 1.5,
                            }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 200 }, children: [_jsx(Typography, { sx: labelSx, children: "View Resume" }), _jsx(TextField, { name: "resume", id: "resume", autoComplete: noAutofill("resume"), fullWidth: true, size: "small", value: vals.resume || "", onChange: set("resume"), placeholder: "Paste resume link or note", sx: fieldSx, inputProps: { autoComplete: noAutofill("resume") }, InputProps: vals.resume && /^https?:\/\//i.test(vals.resume) ? {
                                                endAdornment: (_jsx(Button, { size: "small", href: vals.resume, target: "_blank", rel: "noreferrer", sx: { fontSize: "0.68rem", textTransform: "none", whiteSpace: "nowrap" }, children: "Open" })),
                                            } : undefined })] }), _jsxs(Box, { sx: { flex: 1, minWidth: 200 }, children: [_jsx(Typography, { sx: labelSx, children: "LinkedIn Profile" }), _jsx(TextField, { name: "linkedin", id: "linkedin", autoComplete: noAutofill("linkedin"), fullWidth: true, size: "small", value: vals.linkedin || "", onChange: set("linkedin"), placeholder: "Paste LinkedIn profile link", sx: fieldSx, inputProps: { autoComplete: noAutofill("linkedin") }, InputProps: vals.linkedin && /^https?:\/\//i.test(vals.linkedin) ? {
                                                endAdornment: (_jsx(Button, { size: "small", href: vals.linkedin, target: "_blank", rel: "noreferrer", sx: { fontSize: "0.68rem", textTransform: "none", whiteSpace: "nowrap" }, children: "Open" })),
                                            } : undefined })] })] }), _jsxs(Box, { sx: { bgcolor: "#f0f6ff", border: "1px solid #d0dff5", borderRadius: 1.5, p: 1.5, mb: 2 }, children: [_jsx(Typography, { sx: { fontSize: "0.68rem", fontWeight: 700, color: "#1976d2", textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }, children: "Header" }), _jsxs(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: labelSx, children: "Round" }), _jsx(TextField, { autoComplete: noAutofill("round"), name: "__round", fullWidth: true, size: "small", value: vals.__round || "", onChange: set("__round"), placeholder: "e.g. HR Round", sx: fieldSx, inputProps: { autoComplete: noAutofill("round") } })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: labelSx, children: "Screener" }), _jsx(TextField, { autoComplete: noAutofill("screener"), name: "__screener", fullWidth: true, size: "small", value: vals.__screener || "", onChange: set("__screener"), placeholder: "", sx: fieldSx, inputProps: { autoComplete: noAutofill("screener") } })] })] })] }), SECTIONS.map(section => {
                            const fields = TEMPLATE_FIELDS.filter(f => f.section === section && f.id !== "resume" && f.id !== "linkedin");
                            return (_jsxs(Box, { sx: { mb: 2.5 }, children: [_jsx(Typography, { sx: {
                                            fontSize: "0.7rem", fontWeight: 700, color: "#5a6a85",
                                            textTransform: "uppercase", letterSpacing: 0.8,
                                            mb: 1.2, pb: 0.6, borderBottom: "1.5px solid #e4eaf4",
                                            display: "flex", alignItems: "center", gap: 1,
                                            "&::before": {
                                                content: '""', display: "inline-block",
                                                width: "3px", height: "10px", borderRadius: "2px",
                                                backgroundColor: "#1976d2",
                                            },
                                        }, children: section }), _jsx(Box, { sx: { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.75 }, children: fields.map((field) => {
                                            const { id, label } = field;
                                            const full = "full" in field && field.full;
                                            const multiline = "multiline" in field && field.multiline;
                                            return (_jsxs(Box, { sx: { gridColumn: full ? "1 / -1" : undefined }, children: [_jsx(Typography, { sx: labelSx, children: label }), id === "maritalStatus" ? (_jsxs("select", { name: id, id: id, autoComplete: "off", value: vals[id] || "", onChange: set(id), style: {
                                                            fontSize: "0.78rem", padding: "5px 8px",
                                                            border: "1.5px solid #d0daea", borderRadius: "6px",
                                                            width: "100%", backgroundColor: "#fff",
                                                            color: vals[id] ? "#1a1a2e" : "#aaa",
                                                            outline: "none", fontFamily: "inherit",
                                                            cursor: "pointer",
                                                        }, children: [_jsx("option", { value: "", children: "Select" }), _jsx("option", { value: "Married", children: "Married" }), _jsx("option", { value: "Single", children: "Single" }), _jsx("option", { value: "Widowed", children: "Widowed" }), _jsx("option", { value: "Divorced", children: "Divorced" })] })) : (_jsx(TextField, { name: id, id: id, autoComplete: noAutofill(id), fullWidth: true, size: "small", value: vals[id] || "", onChange: set(id), multiline: !!multiline, rows: multiline ? 3 : undefined, sx: fieldSx, inputProps: { autoComplete: noAutofill(id) } }))] }, id));
                                        }) })] }, section));
                        })] })) }), _jsxs(DialogActions, { sx: { px: 2.5, py: 1.5, gap: 1, borderTop: "1px solid #e4eaf4" }, children: [_jsx(Button, { size: "small", onClick: handleClear, sx: { fontSize: "0.75rem", textTransform: "none", color: "#6b7a99" }, children: "Clear all" }), _jsx(Box, { sx: { flex: 1 } }), _jsx(Button, { size: "small", onClick: onClose, sx: { fontSize: "0.75rem", textTransform: "none" }, children: "Cancel" }), _jsx(Button, { size: "small", variant: "contained", onClick: handleInsert, sx: {
                            fontSize: "0.75rem", textTransform: "none",
                            background: "linear-gradient(135deg, #597e37 0%, #3d5420 100%)",
                        }, children: "Insert into feedback" })] })] }));
};
